// Dono: B3. Botão/menu "Adicionar a uma coleção" usado no detalhe (Detail.tsx monta) e a lista de coleções do lote.
import { useEffect, useId, useRef, useState } from "preact/hooks";
import type { Card, Space } from "../../../../shared/types.ts";
import { normalizeTag } from "../../../../shared/normalize.ts";
import { IconRing } from "../Icons.tsx";
import { failed, smartSpace, store, toast } from "../state.ts";
import { IconCheckSmall, IconRingPlus } from "./icons.tsx";
import { nextColor } from "./order.ts";

/**
 * Lista vazia. `hidden`: automáticas que o lote tirou da lista (D28): elas se enchem pela busca, então o caminho é criar
 * uma manual pelo campo de cima.
 */
function emptyLine(q: string, hidden: Space[]): string {
  const same = hidden.find((s) => s.normalized === q);
  if (q && same) return `“${same.name}” é automática: se enche sozinha pela busca. Digite outro nome para criar uma coleção manual.`;
  if (q) return "Nenhuma coleção com esse nome.";
  if (hidden.length) return "Coleções automáticas se enchem sozinhas pela busca, por isso não aparecem aqui. Digite um nome acima para criar uma coleção manual.";
  return "Digite um nome para criar a primeira coleção.";
}

/**
 * Lista de coleções com busca e "criar" na mesma linha: digitar um nome que não existe oferece criar. Enter escolhe a
 * única que sobrou ou cria. `manualOnly` tira as automáticas (o lote só põe em coleção manual).
 */
export function SpaceChooser({ isIn, onPick, manualOnly, onEscape }: { isIn: (s: Space) => boolean; onPick: (s: Space) => void; manualOnly?: boolean; onEscape: () => void }) {
  const id = useId();
  const [find, setFind] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  // No toque o foco vai para a lista, não para o campo: o teclado abriria e esconderia metade das coleções.
  useEffect(() => (matchMedia("(pointer: coarse)").matches ? box.current : input.current)?.focus(), []);
  const q = normalizeTag(find);
  const all = store.spaces.value.filter((s) => !manualOnly || s.kind === "manual");
  const list = all.filter((s) => !q || s.normalized.includes(q));
  const exact = all.find((s) => s.normalized === q);
  const canCreate = !!q && !store.spaces.value.some((s) => s.normalized === q);
  const create = async () => {
    const name = find.trim();
    try {
      const sid = await store.createSpace({ name, color: nextColor(store.spaces.value.map((s) => s.color)) });
      const s = sid ? store.spaces.value.find((x) => x.id === sid) : undefined;
      if (s) onPick(s);
      setFind("");
    } catch {
      failed();
    }
  };
  return (
    <div
      class="chooser"
      ref={box}
      tabIndex={-1}
      role="group"
      aria-label="Coleções"
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.preventDefault(); // Esc fecha a lista, não o detalhe
        e.stopPropagation();
        onEscape();
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const only = exact ?? (list.length === 1 ? list[0] : undefined);
          if (only) onPick(only);
          else if (canCreate) void create();
        }}
      >
        <input
          ref={input}
          class="chooser-find"
          type="search"
          aria-label="Buscar ou criar coleção"
          aria-describedby={`${id}-n`}
          placeholder="Buscar ou criar coleção…"
          autocomplete="off"
          autocapitalize="none"
          autocorrect="off"
          spellcheck={false}
          enterkeyhint="go"
          maxLength={80}
          value={find}
          onInput={(e) => setFind(e.currentTarget.value)}
        />
      </form>
      <p id={`${id}-n`} class="sr-only" aria-live="polite">
        {list.length === 1 ? "1 coleção" : `${list.length} coleções`}
        {canCreate ? `. Enter cria “${find.trim()}”.` : ""}
      </p>
      <ul class="chooser-list">
        {list.map((s) => {
          const on = isIn(s);
          return (
            <li key={s.id}>
              <button type="button" class="chooser-item" aria-pressed={on} onClick={() => onPick(s)}>
                <span class="chooser-ring" style={`--ring:${s.color}`} aria-hidden="true">
                  {on && <IconCheckSmall />}
                </span>
                <span class="chooser-name">{s.name}</span>
                {s.kind === "smart" && <span class="chooser-kind">automática</span>}
              </button>
            </li>
          );
        })}
        {canCreate && (
          <li class="chooser-new">
            <button type="button" class="chooser-item" onClick={() => void create()}>
              <IconRingPlus />
              <span class="chooser-name">Criar “{find.trim()}”</span>
            </button>
          </li>
        )}
        {!list.length && !canCreate && <li class="chooser-empty">{emptyLine(q, manualOnly ? store.spaces.value.filter((s) => s.kind === "smart") : [])}</li>}
      </ul>
    </div>
  );
}

/**
 * Botão "Adicionar a uma coleção" do detalhe: abre a lista para cima; cada coleção liga e desliga o card.
 * Numa automática, desligar vira exclusão (vale em todos os aparelhos) e ligar inclui o card à mão.
 */
export function AddToSpaceButton({ card }: { card: Card }) {
  const [open, setOpen] = useState(false);
  const [smartIn, setSmartIn] = useState<ReadonlySet<string>>(new Set());
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const id = useId();
  const rows = store.spaceCards.value;
  // Automáticas: o card está nelas se a consulta o acha (e não foi excluído) ou se foi incluído à mão.
  useEffect(() => {
    if (!open) return;
    let live = true;
    const smart = store.spaces.value.filter((s) => s.kind === "smart");
    Promise.all(smart.map((s) => smartSpace(s.id).then((ids) => [s.id, ids.includes(card.id)] as const))).then(
      (r) => live && setSmartIn(new Set(r.filter(([, has]) => has).map(([sid]) => sid))),
      () => {},
    );
    return () => void (live = false);
  }, [open, card.id]);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  const isIn = (s: Space) => {
    if (s.kind === "manual") return card.spaceIds.includes(s.id);
    const row = rows.get(s.id)?.find((r) => r.cardId === card.id);
    return row ? !row.excluded : smartIn.has(s.id);
  };
  const pick = (s: Space) => {
    const on = isIn(s);
    const run = on ? store.removeFromSpace(s.id, card.id) : store.addToSpace(s.id, card.id);
    run.then(() => toast({ key: `ats:${s.id}:${card.id}:${on}`, text: on ? `Tirado de “${s.name}”.` : `Posto em “${s.name}”.` }), failed);
  };
  const close = () => {
    setOpen(false);
    btn.current?.focus();
  };
  return (
    <div class="ats" ref={wrap}>
      <button
        type="button"
        class="round"
        ref={btn}
        aria-label="Adicionar a uma coleção"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        data-tip={open ? undefined : "Adicionar a uma coleção"}
        onClick={() => setOpen(!open)}
      >
        <IconRing />
      </button>
      {open && (
        <>
          {/* Celular: a lista vira folha embaixo; o véu fecha sem tocar no que está atrás. */}
          <div class="ats-scrim" aria-hidden="true" onClick={() => setOpen(false)} />
          <div id={id} class="ats-pop">
            <SpaceChooser isIn={isIn} onPick={pick} onEscape={close} />
          </div>
        </>
      )}
    </div>
  );
}
